import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import Home from "./pages/Home";
import ServiceDetail from "./pages/ServiceDetail";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import { PartnerAuthProvider } from "./contexts/PartnerAuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import ServicesManagement from "./pages/admin/ServicesManagement";
import StagesManagement from "./pages/admin/StagesManagement";
import PartnersManagement from "./pages/admin/PartnersManagement";
import InquiriesManagement from "./pages/admin/InquiriesManagement";
import BookingsManagement from "./pages/admin/BookingsManagement";
import TimeSlotsManagement from "./pages/admin/TimeSlotsManagement";
import TestimonialsManagement from "./pages/admin/TestimonialsManagement";
import BlogManagement from "./pages/admin/BlogManagement";
import FAQManagement from "./pages/admin/FAQManagement";
import PackagesManagement from "./pages/admin/PackagesManagement";
import EmailTemplatesManagement from "./pages/admin/EmailTemplatesManagement";
import SettingsManagement from "./pages/admin/SettingsManagement";
import AnalyticsManagement from "./pages/admin/AnalyticsManagement";
import PartnerRevenueBreakdown from "./pages/admin/PartnerRevenueBreakdown";
import PartnerLogin from "./pages/partner/PartnerLogin";
import PartnerLayout from "./pages/partner/PartnerLayout";
import PartnerDashboard from "./pages/partner/Dashboard";
import ClientsManagement from "./pages/partner/ClientsManagement";
import ClientDetail from "./pages/partner/ClientDetail";
import ProtectedPartnerRoute from "./components/ProtectedPartnerRoute";
import PartnerProfile from "./pages/partner/PartnerProfile";
import { UserAuthProvider } from "./contexts/UserAuthContext";
import ProtectedUserRoute from "./components/ProtectedUserRoute";
import UserDashboard from "./pages/UserDashboard";
import UserServicePage from "./pages/UserServicePage";
import { HRMSAuthProvider } from "./contexts/HRMSAuthContext";
import ProtectedHRMSRoute from "./components/ProtectedHRMSRoute";
import HRMSLogin from "./pages/hrms/HRMSLogin";
import HRMSDashboard from "./pages/hrms/HRMSDashboard";
import HRMSRoleManagement from "./pages/hrms/HRMSRoleManagement";
import EmployeeList from "./pages/hrms/EmployeeList";
import EmployeeForm from "./pages/hrms/EmployeeForm";
import EmployeeProfile from "./pages/hrms/EmployeeProfile";
import OrganizationManagement from "./pages/hrms/OrganizationManagement";
import AttendanceManagement from "./pages/hrms/AttendanceManagement";
import LeaveManagement from "./pages/hrms/LeaveManagement";
import SalaryManagement from "./pages/hrms/SalaryManagement";
import HRMSLayout from "./pages/hrms/HRMSLayout";

function App() {
  return (
    <SettingsProvider>
      <PartnerAuthProvider>
      <UserAuthProvider>
      <HRMSAuthProvider>
      <AdminAuthProvider>
        <div className="App">
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/service/:serviceId" element={<ServiceDetail />} />
            <Route path="/dashboard" element={<ProtectedUserRoute><UserDashboard /></ProtectedUserRoute>} />
            <Route path="/dashboard/services/:serviceId" element={<ProtectedUserRoute><UserServicePage /></ProtectedUserRoute>} />

            {/* HRMS Routes */}
            <Route path="/hrms/login" element={<HRMSLogin />} />
            <Route path="/hrms" element={<ProtectedHRMSRoute><HRMSLayout /></ProtectedHRMSRoute>}>
              <Route index element={<HRMSDashboard />} />
              <Route path="dashboard" element={<HRMSDashboard />} />
              <Route path="roles" element={<ProtectedHRMSRoute requiredPermission="settings.manage"><HRMSRoleManagement /></ProtectedHRMSRoute>} />
              <Route path="organization" element={<ProtectedHRMSRoute requiredPermission="organization.view"><OrganizationManagement /></ProtectedHRMSRoute>} />
              <Route path="attendance" element={<ProtectedHRMSRoute requiredPermission="attendance.view"><AttendanceManagement /></ProtectedHRMSRoute>} />
              <Route path="leave" element={<ProtectedHRMSRoute requiredPermission="leave.view"><LeaveManagement /></ProtectedHRMSRoute>} />
              <Route path="leave/apply" element={<ProtectedHRMSRoute requiredPermission="leave.apply"><LeaveManagement /></ProtectedHRMSRoute>} />
              <Route path="leave/balance" element={<ProtectedHRMSRoute requiredPermission="leave.view"><LeaveManagement /></ProtectedHRMSRoute>} />
              <Route path="leave/calendar" element={<ProtectedHRMSRoute requiredPermission="leave.view"><LeaveManagement /></ProtectedHRMSRoute>} />
              <Route path="salary" element={<ProtectedHRMSRoute requiredPermission="hrms.access"><SalaryManagement /></ProtectedHRMSRoute>} />
              <Route path="employees" element={<ProtectedHRMSRoute requiredPermission="employees.view"><EmployeeList /></ProtectedHRMSRoute>} />
              <Route path="employees/new" element={<ProtectedHRMSRoute requiredPermission="employees.create"><EmployeeForm /></ProtectedHRMSRoute>} />
              <Route path="employees/:id" element={<ProtectedHRMSRoute requiredPermission="employees.view"><EmployeeProfile /></ProtectedHRMSRoute>} />
              <Route path="employees/:id/edit" element={<ProtectedHRMSRoute requiredPermission="employees.view"><EmployeeForm /></ProtectedHRMSRoute>} />
            </Route>
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="services" element={<ServicesManagement />} />
              <Route path="stages" element={<StagesManagement />} />
              <Route path="partners" element={<PartnersManagement />} />
              <Route path="partners/:partnerId/breakdown" element={<PartnerRevenueBreakdown />} />
              <Route path="partners/:partnerId/clients/:clientId/breakdown" element={<PartnerRevenueBreakdown />} />
              <Route path="inquiries" element={<InquiriesManagement />} />
              <Route path="bookings" element={<BookingsManagement />} />
              <Route path="timeslots" element={<TimeSlotsManagement />} />
              <Route path="testimonials" element={<TestimonialsManagement />} />
              <Route path="blogs" element={<BlogManagement />} />
              <Route path="faqs" element={<FAQManagement />} />
              <Route path="packages" element={<PackagesManagement />} />
              <Route path="templates" element={<EmailTemplatesManagement />} />
              <Route path="settings" element={<SettingsManagement />} />
              <Route path="analytics" element={<AnalyticsManagement />} />
            </Route>
            {/* Partner Routes */}
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route
              path="/partner"
              element={
                <ProtectedPartnerRoute>
                  <PartnerLayout />
                </ProtectedPartnerRoute>
              }
            >
              <Route path="dashboard" element={<PartnerDashboard />} />
              <Route path="clients" element={<ClientsManagement />} />
              <Route path="clients/:clientId" element={<ClientDetail />} />
                        <Route path="profile" element={<PartnerProfile />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </div>
      </AdminAuthProvider>
      </HRMSAuthProvider>
      </UserAuthProvider>
      </PartnerAuthProvider>
    </SettingsProvider>
  );
}

export default App;
